import { getUserColor } from "@fieldbook/common/utils"
import { length, lineString } from "@turf/turf"
import type { PowerSyncTrackRow } from "@/lib/powersync/schema"
import type { Track, TrackPoint } from "@/lib/tracksDb"

export type TrackProperties = {
  trackId: string
  /** Who recorded the track. Absent on getTrackGeoJson's raw output and on a
   *  synced row without `created_by`; getLocalTrackGeoJson always sets it. */
  createdBy?: string
  /** The recorder's colour, as on their map marker, so the map can draw each
   *  person's tracks in it. Derived from `createdBy` — see getUserColor. */
  color?: string
  pointCount: number
  lengthM: number
  startTime: number
  endTime: number | undefined
  status: Track["status"]
  displayOnMap?: boolean
}

export const getTrackGeoJson = (track: Track, trackPoints?: TrackPoint[]) => {
  const points = trackPoints?.map((point) => point.point) ?? []

  return {
    type: "Feature" as const,
    geometry: {
      type: "LineString" as const,
      coordinates: trackPoints?.map((point) => point.point) ?? [],
    },
    properties: {
      trackId: track.id,
      lengthM:
        points.length > 1 ? length(lineString(points), { units: "meters" }) : 0,
      pointCount: trackPoints?.length ?? 0,
      startTime: track.timestamp,
      endTime: trackPoints?.[trackPoints.length - 1]?.timestamp,
      status: track.status,
      displayOnMap: track.displayOnMap,
    },
  }
}

/**
 * Whether the signed-in user may see a track held in IndexedDB: their own, or
 * a legacy track with no owner yet (which the next publish pass claims for
 * them). A track another account recorded on a shared device is never theirs.
 */
export const isOwnLocalTrack = (track: Track, userId: string | undefined) =>
  Boolean(userId) && (track.ownerId === undefined || track.ownerId === userId)

/**
 * The LineString Feature for a track still held in IndexedDB, stamped with its
 * recorder and their colour like getSyncedTrackGeoJson's output. Returns null
 * for a track the signed-in user may not see — see isOwnLocalTrack.
 */
export const getLocalTrackGeoJson = (
  track: Track,
  trackPoints: TrackPoint[] | undefined,
  userId: string | undefined,
): GeoJSON.Feature<GeoJSON.LineString, TrackProperties> | null => {
  if (!userId || !isOwnLocalTrack(track, userId)) return null

  const feature = getTrackGeoJson(track, trackPoints)
  return {
    ...feature,
    properties: {
      ...feature.properties,
      createdBy: userId,
      color: getUserColor(userId).color,
    },
  }
}

type SharedLocalFeature = {
  track: Track
  userId: string | undefined
  feature: GeoJSON.Feature<GeoJSON.LineString, TrackProperties> | null
}

const sharedLocalFeatures = new WeakMap<TrackPoint[], SharedLocalFeature>()

/**
 * getLocalTrackGeoJson, built once per points array. Every component showing
 * the active track reads the same `track` and `trackPoints` objects from the
 * query cache, so they share one LineString (and one length calculation)
 * instead of each rebuilding it on every recorded batch.
 */
export const getSharedLocalTrackGeoJson = (
  track: Track,
  trackPoints: TrackPoint[] | undefined,
  userId: string | undefined,
) => {
  if (!trackPoints) return getLocalTrackGeoJson(track, trackPoints, userId)

  const shared = sharedLocalFeatures.get(trackPoints)
  if (shared?.track === track && shared.userId === userId) {
    return shared.feature
  }

  const feature = getLocalTrackGeoJson(track, trackPoints, userId)
  sharedLocalFeatures.set(trackPoints, { track, userId, feature })
  return feature
}

export type SyncedTrackRow = Pick<
  PowerSyncTrackRow,
  | "id"
  | "created_by"
  | "geojson"
  | "started_at"
  | "ended_at"
  | "length_m"
  | "point_count"
>

const parseTime = (value: string | null | undefined) => {
  if (!value) return undefined
  const time = Date.parse(value)
  return Number.isNaN(time) ? undefined : time
}

/**
 * The LineString Feature for a track row synced through PowerSync, shaped like
 * getTrackGeoJson's output so callers don't care where a track came from.
 * Properties come from the row's columns rather than the stored Feature's own
 * `properties`, which only the recording device wrote. Returns null for a row
 * whose `geojson` isn't a usable LineString (a malformed row must never crash
 * the map).
 */
export const getSyncedTrackGeoJson = (
  row: SyncedTrackRow,
  displayOnMap: boolean,
): GeoJSON.Feature<GeoJSON.LineString, TrackProperties> | null => {
  let stored: unknown
  try {
    stored = row.geojson ? JSON.parse(row.geojson) : null
  } catch {
    return null
  }

  const geometry =
    stored && typeof stored === "object" && "geometry" in stored
      ? (stored.geometry as GeoJSON.LineString | null)
      : null
  if (geometry?.type !== "LineString" || !Array.isArray(geometry.coordinates)) {
    return null
  }

  return {
    type: "Feature",
    geometry,
    properties: {
      trackId: row.id,
      createdBy: row.created_by ?? undefined,
      color: row.created_by ? getUserColor(row.created_by).color : undefined,
      lengthM: row.length_m ?? 0,
      pointCount: row.point_count ?? geometry.coordinates.length,
      startTime: parseTime(row.started_at) ?? 0,
      endTime: parseTime(row.ended_at),
      status: "completed",
      displayOnMap,
    },
  }
}
